import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

export default function ResetAllData({ onDone }) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const doReset = async () => {
    try {
      await api.post("/admin/reset-all");
      toast.success("All transactional data wiped. Master data retained.");
      setOpen(false);
      setConfirmText("");
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="danger-zone" className="section-card border-2 border-red-200 bg-red-50/40 mt-8">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-red-100 text-red-600 flex items-center justify-center shrink-0">
          <AlertTriangle size={20} />
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-semibold text-red-800">Danger Zone — Reset All Data</h3>
          <p className="text-sm text-red-700/80 mt-1">
            Permanently deletes all transactional records (DPR, stock, power, gate passes, process stock,
            buffing, chemical batches, ledgers & approvals). Master data (users, processes, models, BOM) is kept.
          </p>
          <button
            data-testid="reset-all-btn"
            onClick={() => setOpen(true)}
            className="mt-3 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
          >
            Reset All Data
          </button>
        </div>
      </div>

      <AlertDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setConfirmText(""); }}>
        <AlertDialogContent data-testid="reset-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset all transactional data?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. Type <b>RESET</b> below to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <input
            data-testid="reset-confirm-input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type RESET"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
          />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="reset-confirm-btn"
              disabled={confirmText !== "RESET"}
              onClick={doReset}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40 disabled:pointer-events-none"
            >
              Wipe Data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
